import React, { useState } from "react";

import {
  AttachMoney as MoneyIcon,
  NotificationsActive as BellIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  HourglassEmpty as LoadingIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  Tooltip,
  Typography,
  keyframes,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { useActionNotifications } from "../../contexts";
import { usePendingFeeReview } from "../../hooks/useFeeInformationFlyer";
import { useAppSelector } from "../../store/hooks";

// Glowing pulse animation for the fee notification icon
const glowPulse = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(255, 152, 0, 0.7), 0 0 10px rgba(255, 152, 0, 0.5);
  }
  50% {
    box-shadow: 0 0 20px rgba(255, 152, 0, 0.9), 0 0 30px rgba(255, 152, 0, 0.7);
  }
  100% {
    box-shadow: 0 0 5px rgba(255, 152, 0, 0.7), 0 0 10px rgba(255, 152, 0, 0.5);
  }
`;

export const HeaderNotifications: React.FC = () => {
  const _selectedService = useAppSelector((state) => state.service.selectedService);
  const { notifications, hasActiveNotifications, removeNotification, clearAllNotifications } = useActionNotifications();
  const navigate = useNavigate();

  // Notification menu state
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);

  // Check if there are pending fees that need review
  const { hasPendingFees } = usePendingFeeReview();

  // Notification bell handlers
  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchor(null);
  };

  const handleRemoveNotification = (id: string) => {
    removeNotification(id);
  };

  const handleClearAllNotifications = () => {
    clearAllNotifications();
    handleNotificationMenuClose();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "running":
        return <LoadingIcon sx={{ color: "#1976d2" }} />;
      case "completed":
        return <CheckIcon sx={{ color: "#4caf50" }} />;
      case "error":
        return <ErrorIcon sx={{ color: "#f44336" }} />;
      default:
        return <LoadingIcon />;
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return timestamp.toLocaleDateString();
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {/* Fee Review Notification - High Priority Alert */}
      {hasPendingFees && (
        <Tooltip title="IMPORTANT: Fee Review Required - Click to Review">
          <IconButton
            onClick={() => navigate("/fee-information")}
            sx={{
              color: "#ff9800",
              backgroundColor: "rgba(255, 152, 0, 0.1)",
              borderRadius: "50%",
              animation: `${glowPulse} 2s ease-in-out infinite`,
              "&:hover": {
                backgroundColor: "rgba(255, 152, 0, 0.2)",
                transform: "scale(1.1)",
              },
              transition: "transform 0.2s ease-in-out",
            }}
          >
            <Badge
              badgeContent="!"
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "1rem",
                  fontWeight: "bold",
                  height: "22px",
                  minWidth: "22px",
                },
              }}
            >
              <MoneyIcon sx={{ fontSize: "1.5rem" }} />
            </Badge>
          </IconButton>
        </Tooltip>
      )}

      {/* Notification Bell - Shows when there are active notifications */}
      {hasActiveNotifications && (
        <>
          <Tooltip title="View Action Notifications">
            <IconButton
              onClick={handleNotificationMenuOpen}
              sx={{
                color: "#1976d2",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.1)",
                },
              }}
            >
              <Badge
                badgeContent={notifications.filter((n) => n.status === "running").length || notifications.length}
                color="primary"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.75rem",
                  },
                }}
              >
                <BellIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Notification Menu */}
          <Menu
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={handleNotificationMenuClose}
            sx={{
              "& .MuiPaper-root": {
                borderRadius: "8px",
                minWidth: "350px",
                maxWidth: "400px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              },
            }}
            transformOrigin={{
              horizontal: "right",
              vertical: "top",
            }}
            anchorOrigin={{
              horizontal: "right",
              vertical: "bottom",
            }}
          >
            <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                  Action Notifications
                </Typography>
                {notifications.length > 0 && (
                  <Button
                    size="small"
                    onClick={handleClearAllNotifications}
                    sx={{
                      color: "#666",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    Clear All
                  </Button>
                )}
              </Box>
            </Box>

            {notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="textSecondary">
                  No notifications
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0, maxHeight: "400px", overflowY: "auto" }}>
                {notifications.map((notification, index) => (
                  <ListItem
                    key={notification.id}
                    sx={{
                      borderBottom: index < notifications.length - 1 ? "1px solid #f0f0f0" : "none",
                      "&:hover": {
                        backgroundColor: "#f9f9f9",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>{getStatusIcon(notification.status)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {notification.title}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveNotification(notification.id)}
                            sx={{ ml: 1 }}
                          >
                            <ClearIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {notification.message}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatTimestamp(notification.timestamp)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Menu>
        </>
      )}
    </Box>
  );
};
